import React, { RefObject, useEffect, useState, useCallback, useMemo } from 'react';
import { formatBuildPanelTimestamp } from './buildPanelTimestamp';
import { formatAiActivitySummaryLabel, summariseAiGroup } from './ai-activity-utils';
import { AiActivitySummaryRichHeader } from './ai-activity-summary-header';
import type { AiActivitySummary } from '../../../../../shared/lib/buildStatusTracker';

export type MessageCategory = 'status' | 'error' | 'ai_thinking' | 'ai_tool_usage' | 'ai_text';

export interface ProgressStatusMessage {
    id: string;
    type: 'status' | 'error';
    content: string;
    agent?: string;
    llm?: string;
    progress?: number | null;
    timestamp: Date;
    uiid?: string;
    featureName?: string;
    backendId?: string;
    backendTs?: string;
    category?: MessageCategory;
    /** For ai_tool_usage: Read, Write, Bash, etc. (from backend text) */
    toolUsageKind?: string;
}

const AI_CATEGORIES = new Set<MessageCategory>(['ai_thinking', 'ai_tool_usage', 'ai_text']);

/** Number of newest AI lines shown without expanding "Earlier" */
const VISIBLE_AI_TAIL_COUNT = 2;

/** Max lines rendered inside the collapsible "Earlier" block (newest-of-older, nearest the tail). */
const MAX_EARLIER_MESSAGES = 20;

function isAiMessage(msg: ProgressStatusMessage): boolean {
    return !!msg.category && AI_CATEGORIES.has(msg.category);
}

type DisplayItem =
    | { kind: 'message'; message: ProgressStatusMessage }
    | { kind: 'ai_group'; id: string; messages: ProgressStatusMessage[] };

function buildDisplayItems(sorted: ProgressStatusMessage[]): DisplayItem[] {
    const items: DisplayItem[] = [];
    let pendingAi: ProgressStatusMessage[] = [];

    const flushAi = () => {
        if (pendingAi.length > 0) {
            items.push({ kind: 'ai_group', id: `ai-group-${pendingAi[0].id}`, messages: [...pendingAi] });
            pendingAi = [];
        }
    };

    for (const msg of sorted) {
        if (isAiMessage(msg)) {
            pendingAi.push(msg);
        } else {
            flushAi();
            items.push({ kind: 'message', message: msg });
        }
    }
    flushAi();
    return items;
}

function categoryIcon(category?: MessageCategory): string {
    if (category === 'ai_tool_usage') return '🔧';
    if (category === 'ai_thinking') return '💭';
    return '💬';
}

function categoryColor(category?: MessageCategory): string {
    if (category === 'ai_tool_usage') return 'text-amber-500/70';
    if (category === 'ai_thinking') return 'text-gray-500';
    return 'text-gray-400';
}

// ---------- Sub-components ----------

const StatusRow: React.FC<{ message: ProgressStatusMessage }> = ({ message }) => {
    const isError = message.type === 'error';
    return (
        <div
            className={`py-0.5 px-1 transition-colors font-mono ${
                isError ? 'hover:bg-red-950/20 bg-red-950/10' : 'hover:bg-white/5'
            }`}
        >
            <div className="text-xs leading-tight">
                <span className="text-gray-500">{formatBuildPanelTimestamp(message.timestamp)}</span>
                <span className="mx-1">:</span>
                {message.uiid && (
                    <>
                        <span className="text-purple-400 font-semibold">
                            Progress-{message.uiid.substring(0, 8)}
                        </span>
                        <span className="mx-1">:</span>
                    </>
                )}
                {message.agent ? (
                    <>
                        <span className={isError ? 'text-red-400 font-semibold' : 'text-blue-400 font-semibold'}>
                            [{message.agent}
                        </span>
                        <span className="text-gray-500">({message.llm || 'None'})</span>
                        <span className={isError ? 'text-red-400' : 'text-blue-400'}>]</span>
                    </>
                ) : (
                    <span className={isError ? 'text-red-400 font-semibold' : 'text-blue-400 font-semibold'}>
                        [{isError ? 'ERROR' : 'STATUS'}]
                    </span>
                )}
                <span className="mx-1">:</span>
                <span className={isError ? 'text-red-300' : 'text-gray-300'}>{message.content}</span>
            </div>
        </div>
    );
};

const AiMessageLine: React.FC<{ message: ProgressStatusMessage }> = ({ message: msg }) => (
    <div className="py-0.5 px-2 hover:bg-white/5 transition-colors font-mono">
        <div className="text-[11px] leading-tight flex gap-1">
            <span className="text-gray-600 shrink-0">{formatBuildPanelTimestamp(msg.timestamp)}</span>
            <span className="shrink-0">{categoryIcon(msg.category)}</span>
            <span className={`break-all ${categoryColor(msg.category)}`}>{msg.content}</span>
        </div>
    </div>
);

const AiActivityGroup: React.FC<{
    messages: ProgressStatusMessage[];
    defaultExpanded: boolean;
    headerSummaryLabel: string;
    richSummary?: AiActivitySummary | null;
}> = ({ messages, defaultExpanded, headerSummaryLabel, richSummary }) => {
    const { olderMessages, tailMessages } = useMemo(() => {
        if (messages.length <= VISIBLE_AI_TAIL_COUNT) {
            return { olderMessages: [] as ProgressStatusMessage[], tailMessages: messages };
        }
        const olderFull = messages.slice(0, -VISIBLE_AI_TAIL_COUNT);
        const olderCapped =
            olderFull.length > MAX_EARLIER_MESSAGES
                ? olderFull.slice(-MAX_EARLIER_MESSAGES)
                : olderFull;
        return {
            olderMessages: olderCapped,
            tailMessages: messages.slice(-VISIBLE_AI_TAIL_COUNT)
        };
    }, [messages]);

    const [earlierExpanded, setEarlierExpanded] = useState(defaultExpanded);

    useEffect(() => {
        setEarlierExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const toggleEarlier = useCallback(() => setEarlierExpanded(prev => !prev), []);

    return (
        <div className="my-0.5">
            <div
                className="pl-4 pr-2 py-1 rounded bg-gray-800/25 text-[11px] font-mono text-gray-600
                           border-l-2 border-gray-700/50"
            >
                {richSummary ? (
                    <AiActivitySummaryRichHeader summary={richSummary} />
                ) : (
                    <>AI Activity ({headerSummaryLabel})</>
                )}
            </div>

            {olderMessages.length > 0 && (
                <>
                    <button
                        type="button"
                        onClick={toggleEarlier}
                        className="w-full flex items-center gap-1.5 pl-4 pr-2 py-0.5 mt-0.5 rounded
                                   bg-gray-800/30 hover:bg-gray-800/50 transition-colors
                                   text-[10px] font-mono text-gray-500 hover:text-gray-400 cursor-pointer
                                   border-l-2 border-gray-700/40"
                    >
                        <span className="select-none">{earlierExpanded ? '▾' : '▸'}</span>
                        <span>
                            Earlier ({olderMessages.length} message{olderMessages.length > 1 ? 's' : ''})
                        </span>
                    </button>
                    {earlierExpanded && (
                        <div
                            className="ml-4 border-l-2 border-gray-800/60 overflow-y-auto"
                            style={{
                                maxHeight: '200px',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#374151 #0A071B'
                            }}
                        >
                            {olderMessages.map(msg => (
                                <AiMessageLine key={msg.id} message={msg} />
                            ))}
                        </div>
                    )}
                </>
            )}

            <div
                className={`ml-4 border-l-2 border-gray-800/60 ${olderMessages.length > 0 ? 'mt-0.5' : ''}`}
            >
                {tailMessages.map(msg => (
                    <AiMessageLine key={msg.id} message={msg} />
                ))}
            </div>
        </div>
    );
};

// ---------- Main component ----------

interface BuildProgressTabProps {
    progressStatusMessages: ProgressStatusMessage[];
    progressTabEndRef: RefObject<HTMLDivElement | null>;
    /** Cumulative AI counts for the current build (persists past the 20 stored lines). */
    aiActivitySummary?: AiActivitySummary | null;
}

const BuildProgressTab: React.FC<BuildProgressTabProps> = ({
    progressStatusMessages,
    progressTabEndRef,
    aiActivitySummary
}) => {
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        if (progressTabEndRef.current) {
            progressTabEndRef.current.scrollTop = progressTabEndRef.current.scrollHeight;
        }
    }, [progressStatusMessages, progressTabEndRef]);

    useEffect(() => {
        if (progressTabEndRef.current) {
            progressTabEndRef.current.scrollTop = progressTabEndRef.current.scrollHeight;
        }
    }, [progressTabEndRef]);

    const sorted = [...progressStatusMessages].sort((a, b) => {
        if (!a.backendTs && !b.backendTs) return 0;
        if (!a.backendTs) return 1;
        if (!b.backendTs) return -1;
        return a.backendTs.localeCompare(b.backendTs);
    });

    const displayItems = buildDisplayItems(sorted);
    const hasAiGroups = displayItems.some(item => item.kind === 'ai_group');
    const aiGroupCount = useMemo(
        () => displayItems.filter(item => item.kind === 'ai_group').length,
        [displayItems]
    );

    const toggleExpandAll = useCallback(() => setExpandAll(prev => !prev), []);

    const buildWideSummaryLabel = useMemo(() => {
        if (aiGroupCount !== 1) return null;
        return formatAiActivitySummaryLabel(aiActivitySummary ?? undefined);
    }, [aiGroupCount, aiActivitySummary]);

    const useRichActivityHeader =
        aiGroupCount === 1 && !!buildWideSummaryLabel && !!aiActivitySummary;

    return (
        <div
            ref={progressTabEndRef}
            className="flex-1 overflow-y-auto p-3 bg-[#0A071B]"
            style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#4B5563 #0A071B',
                scrollBehavior: 'smooth'
            }}
        >
            {progressStatusMessages.length === 0 ? (
                <div className="text-center text-gray-500 text-xs py-8 font-mono">
                    <div className="mb-2 text-gray-400">[INFO] No status updates yet</div>
                    <div className="text-gray-600">
                        Status updates from VishCoder agents will appear here
                    </div>
                </div>
            ) : (
                <>
                    {/* Global expand/collapse toggle for AI activity blocks */}
                    {hasAiGroups && (
                        <div className="flex justify-end mb-1">
                            <button
                                onClick={toggleExpandAll}
                                className="text-[10px] font-mono text-gray-600 hover:text-gray-400
                                           px-2 py-0.5 rounded bg-gray-800/30 hover:bg-gray-800/50
                                           transition-colors cursor-pointer"
                                title={expandAll ? 'Collapse all AI activity' : 'Expand all AI activity'}
                            >
                                {expandAll ? '⊟ Collapse AI' : '⊞ Expand AI'}
                            </button>
                        </div>
                    )}

                    <div className="space-y-0">
                        {displayItems.map(item => {
                            if (item.kind === 'message') {
                                return <StatusRow key={item.message.id} message={item.message} />;
                            }
                            const headerSummaryLabel =
                                buildWideSummaryLabel ?? summariseAiGroup(item.messages);
                            return (
                                <AiActivityGroup
                                    key={item.id}
                                    messages={item.messages}
                                    defaultExpanded={expandAll}
                                    headerSummaryLabel={headerSummaryLabel}
                                    richSummary={useRichActivityHeader ? aiActivitySummary : null}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default BuildProgressTab;
